// §V2 EXACT PORT — App hub (docs/design-spikes/pomela-platform-v2/index.html
// lines 554-706): per-app header (mark + name + truthful status badge +
// View live / Edit app), Overview/Manage segtabs, device preview hosting the
// REAL running app, real share panel (copy link / open / X / WhatsApp), and a
// Manage tab embedding the REAL category-aware management components from
// screens/AppDetail.tsx (server-resolved app row, ownership-checked). The
// spike's data-statehost markup stays — driven by the REAL resolve lifecycle
// (local recents cache → /v1/creator/apps/:id server truth → 404 = honest
// not-found), never by prototype state-switchers.
import { ManageContent } from '../components/ManageContent';
import { useEffect, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { useToast } from '@vibeshell/hooks';
import { Ic, tt } from './icons';
import { t } from '../lib/i18n';
import { goNav, editorHash } from '../lib/nav-items';
import { findRecentApp, type RecentApp } from '../lib/recent-apps';
import { fetchServerApp, type DeliveryRecord, type LaneDescriptor } from '../lib/creator-apps-api';
import { DeliveryNotice } from '../components/DeliveryNotice';
import { capabilityForLane } from '../lib/lane-capability';
import { loadAnalyticsMap, type AppAnalyticsRow } from '../lib/workspace-data';
import { buildShareText, channelShareUrl } from '../lib/share-urls';
import { nextCapabilitiesFor } from '../lib/next-capabilities';
import { AppPreview } from '../components/AppPreview';
import { ErrorState } from '../components/ErrorState';
import {
  AppManagementShell,
  inferCapability,
  hasOwnerConsole,
  CAPABILITY_OWNER_CONSOLES,
  type AppCapability
} from '../components/AppManagementShell';
import { ManageRecords } from '../components/ManageRecords';
import { ManageInventory } from '../components/ManageInventory';
import { ManageBookings } from '../components/ManageBookings';
import { ManageCustomers } from '../components/ManageCustomers';
// §DEF-OWNERCONSOLE-1 (2026-07-12) — CRM's owner_console contract (manage_customers: read/write
// contacts + persisted legal stage transitions) is served by the REAL, fully server-backed ManageCRM
// (contacts + deals CRUD over /owner/{contacts,deals}, deal pipeline via the now-registered
// DEAL_LIFECYCLE, activity timeline). The generic ManageRecords is list+advance only (no contact/deal
// write), so it cannot satisfy the contract — ManageCRM is materially better AND fully server-backed.
import { ManageCRM } from '../components/ManageCRM';
import { BookingAvailability } from '../components/BookingAvailability';
import { PaymentLinkConfig } from '../components/PaymentLinkConfig';
import { WaitlistPanel } from '../components/WaitlistPanel';
import { AssetLibraryPanel } from '../components/AssetLibraryPanel';
import { ActivityLogPanel } from '../components/ActivityLogPanel';
import { ExportSheet } from '../components/ExportSheet';
import { DomainSheet } from '../components/DomainSheet';

// §S679 additive server fields the /v1/creator/apps row carries (D1 truth) —
// the same cast AppDetail uses (the base RecentApp type predates them).
type ShareStateApp = RecentApp & { hasDraft?: boolean; isPublic?: boolean; lastDeployedAt?: number };

type Load =
  | { status: 'loading' }
  | { status: 'ready'; app: RecentApp }
  | { status: 'notfound' }
  | { status: 'error' };

/** Real per-metric formatting — AppDetail's exact rule (— when no row). */
const fmt = (n?: number): string => (typeof n === 'number' ? n.toLocaleString() : '—');

/** Honest truncation for the tagline (real description/prompt, no invention). */
function truncate(s: string, max = 80): string {
  const clean = (s || '').trim().replace(/\s+/g, ' ');
  return clean.length <= max ? clean : `${clean.slice(0, max - 1).trimEnd()}…`;
}

/** The spike's per-category manage title ("Manage bookings…") → the REAL
 *  capability t() keys the App Management Shell already localises. */
const MANAGE_TITLE_KEY: Record<AppCapability, string> = {
  booking: 'ad.mgBookingT',
  commerce: 'ad.mgCommerceT',
  orderahead: 'ad.mgOrderT',
  crm: 'ad.mgCrmT',
  tracker: 'ad.mgTrackerT',
  directory: 'ad.mgDirectoryT',
  community: 'ad.mgCommunityT',
  ai: 'ad.mgAiT',
  ticketing: 'ad.secManage',
  inventory: 'ad.secManage',
  quizzes: 'ad.secManage',
  events: 'ad.secManage',
  hr: 'ad.secManage',
  generic: 'ad.secManage'
};

/** next-capabilities icon vocabulary → the closest spike {i:…} icon. */
const CAP_ICON: Record<string, string> = {
  user: 'user',
  bell: 'bell',
  search: 'search',
  image: 'image',
  lock: 'lock',
  'bar-chart-2': 'chart',
  'trending-up': 'chart',
  'share-2': 'share',
  'shopping-bag': 'coin',
  'shopping-cart': 'coin',
  'credit-card': 'card',
  'user-check': 'check',
  'check-circle': 'check'
};

export function AppHubV2({ appId }: { appId: string }): JSX.Element {
  const [load, setLoad] = useState<Load>({ status: 'loading' });
  const [stats, setStats] = useState<AppAnalyticsRow | undefined>();
  // §CANONICAL-LANE-DESCRIPTOR — the server's authoritative lane set (primary AND every composed
  // secondary, at their real NAMESPACED collection ids).
  const [lanes, setLanes] = useState<LaneDescriptor | undefined>(undefined);
  // §S7-WORKSPACE-LANE — until the server has answered, no capability-specific panel is guessed into view
  const [lanesResolved, setLanesResolved] = useState(false);
  // D80-a/j — the request contract's judgement of the delivery; absent until the server answers
  const [delivery, setDelivery] = useState<DeliveryRecord | undefined>(undefined);
  const [tab, setTab] = useState<'overview' | 'manage'>('overview');
  const [exportOpen, setExportOpen] = useState(false);
  const [domainOpen, setDomainOpen] = useState(false);
  // bumping this re-runs the resolve effect — the error panel's real retry
  const [retryTick, setRetryTick] = useState(0);
  const toast = useToast();

  // §7C-HR — AppDetail's EXACT resolve flow: show the localStorage cache
  // instantly when present, then the SERVER row (gateway → app_metadata,
  // ownership-checked) becomes authoritative. 404 = missing OR not owned →
  // honest not-found. A network failure with no cache = retryable error.
  useEffect(() => {
    let alive = true;
    const cached = findRecentApp(appId);
    setLoad(cached ? { status: 'ready', app: cached } : { status: 'loading' });
    void fetchServerApp(appId).then(({ app: srv, lanes: srvLanes, delivery: srvDelivery, status }) => {
      if (!alive) return;
      setLanes(srvLanes);
      setDelivery(srvDelivery);
      setLanesResolved(true);
      if (srv) setLoad({ status: 'ready', app: srv });
      else if (status === 404) setLoad({ status: 'notfound' });
      // non-404 miss: keep a cached copy when we have one (AppDetail parity);
      // otherwise 200-with-no-row = honest not-found, network/5xx = retryable
      else setLoad((cur) => (cur.status === 'ready' ? cur : status === 200 ? { status: 'notfound' } : { status: 'error' }));
    });
    return () => {
      alive = false;
    };
  }, [appId, retryTick]);

  // Real analytics rollup (/v1/analytics) — AppDetail's exact flow.
  useEffect(() => {
    let alive = true;
    void loadAnalyticsMap().then((m) => {
      if (alive) setStats(m.get(appId));
    });
    return () => {
      alive = false;
    };
  }, [appId]);

  // ── honest not-found (server said missing OR not owned by this creator) ──
  if (load.status === 'notfound') {
    return (
      <section className="screen on" data-screen="app">
        <div className="wrap stack">
          <ErrorState
            variant="notFound"
            title={t('ad.notFound')}
            body={t('ad.notFoundSub')}
            primaryLabel={t('ad.backToWorkspace')}
            onPrimary={() => goNav('apps')}
          />
        </div>
      </section>
    );
  }

  // ── deep-link resolve in flight — the spike's exact loading skeleton ──
  if (load.status === 'loading') {
    return (
      <section className="screen on" data-screen="app">
        <div className="wrap stack">
          <div data-statehost="manage" data-state="loading" aria-hidden="true">
            <div data-when="loading" className="stack">
              <div className="sk sk-line" style="width:120px;height:20px" />
              <div className="stat-grid">
                <div className="stat"><div className="sk sk-line" style="width:44px;height:26px;margin:2px 0 8px" /><div className="sk sk-line" style="width:70%" /></div>
                <div className="stat"><div className="sk sk-line" style="width:44px;height:26px;margin:2px 0 8px" /><div className="sk sk-line" style="width:60%" /></div>
                <div className="stat"><div className="sk sk-line" style="width:44px;height:26px;margin:2px 0 8px" /><div className="sk sk-line" style="width:66%" /></div>
              </div>
              <div className="grid-2">
                <div className="panel panel-p stack" style="gap:14px">
                  <div className="sk sk-line" style="width:40%;height:15px" />
                  <div className="row gap12"><div className="sk" style="width:36px;height:36px;border-radius:11px" /><div style="flex:1"><div className="sk sk-line" style="width:55%" /><div className="sk sk-line" style="width:80%" /></div></div>
                  <div className="row gap12"><div className="sk" style="width:36px;height:36px;border-radius:11px" /><div style="flex:1"><div className="sk sk-line" style="width:48%" /><div className="sk sk-line" style="width:72%" /></div></div>
                  <div className="row gap12"><div className="sk" style="width:36px;height:36px;border-radius:11px" /><div style="flex:1"><div className="sk sk-line" style="width:52%" /><div className="sk sk-line" style="width:66%" /></div></div>
                </div>
                <div className="panel panel-p stack" style="gap:14px">
                  <div className="sk sk-line" style="width:52%;height:15px" />
                  <div className="sk" style="height:96px;border-radius:14px" />
                  <div className="sk" style="height:80px;border-radius:14px" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // ── fetch error with no cached copy — the spike's error panel + real retry ──
  if (load.status === 'error') {
    return (
      <section className="screen on" data-screen="app">
        <div className="wrap stack">
          <div data-statehost="manage" data-state="error">
            <div data-when="error">
              <div className="panel empty">
                <span className="e-ic e-ic-danger"><Ic n="x" /></span>
                <h4>{tt('v2.app.errT', "We couldn't load your data")}</h4>
                <p>{tt('v2.app.errB', "Something went wrong reaching your app's live data. Your customers are unaffected, this is only your private view.")}</p>
                <button className="btn btn-tonal" type="button" onClick={() => setRetryTick((n) => n + 1)}>
                  <Ic n="history" /> {tt('v2.app.retry', 'Try again')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // ── loaded — real app row (server-authoritative) ──────────────────────────
  const app = load.app;
  const share679 = app as ShareStateApp;
  const name = app.manifest?.name || appId;
  const mark = (name.charAt(0) || 'P').toUpperCase();
  // real tagline: the app's own description, else its prompt — never invented
  const tagline = app.manifest?.description || truncate(app.prompt);
  // §S7-WORKSPACE-LANE — the lane the server built the app on decides; the prompt-word guess is the FALLBACK for an
  // unknown lane or an app with no descriptor, and nothing is guessed before the server answers.
  const capability = capabilityForLane(lanes?.primary) ?? (lanesResolved ? inferCapability(`${name} ${app.prompt} ${app.manifest?.description ?? ''}`) : 'generic');
  // §S680 status truth — 'live' only for a deployed (non-preview-fallback) URL.
  const status: 'live' | 'preview' = app.url && !app.url.startsWith('/preview/') ? 'live' : 'preview';
  const draft = share679.hasDraft === true;
  const isPublic = share679.isPublic === true;
  // the spike's renderApp badge behavior, on the REAL publish state
  // D80-a/j — an app whose mandatory requirements are not met is built to try, whatever its address says
  const builtToTry = delivery?.state === 'built_to_try';
  const badgeCls = draft || builtToTry ? 'badge warn dot' : status === 'live' ? 'badge ok dot' : 'badge dot';
  const badgeLabel = builtToTry ? t('dlv.stTry') : draft ? t('wsx.stDraft') : status === 'live' ? t('wsx.stLive') : t('wsx.stPreview');
  // AppDetail's exact share-URL construction (absolute, real preview/deploy URL)
  const shareUrl = app.url ? (app.url.startsWith('http') ? app.url : `${location.origin}${app.url}`) : '';
  const shareDisplay = shareUrl.replace(/^https?:\/\//, '');
  // Honest stat band rule (AppDetail): numbers only when ≥1 metric is real and
  // non-zero; otherwise ONE quiet line — never a row of fabricated zeros.
  const hasAnyStat =
    !!stats &&
    (stats.views ?? 0) + (stats.installs ?? 0) + (stats.interactions ?? 0) + (stats.creditsUsed ?? 0) > 0;
  // REAL per-app suggestions — the deterministic next-capabilities brain
  // (domain-matched on this app's own name + prompt), not invented rows.
  const suggestions = nextCapabilitiesFor(`${name} ${app.prompt}`, [], 2);

  const openEditor = (): void => goNav(editorHash(appId));
  const openLive = (): void => {
    if (shareUrl) window.open(shareUrl, '_blank', 'noopener');
  };
  const copyLink = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.show(t('ac.linkCopied'), { variant: 'success' });
    } catch {
      /* clipboard blocked — the visible URL stays selectable */
    }
  };
  // AppDetail's exact share flow — native sheet when available, else copy+toast
  const share = (): void => {
    if (navigator.share) {
      void navigator.share({ url: shareUrl, title: name }).catch(() => {});
      return;
    }
    void copyLink();
  };
  // real viral channel deep-links (the unit-pinned ShareCard builders)
  const openChannel = (ch: 'x' | 'whatsapp'): void => {
    window.open(channelShareUrl(ch, buildShareText(name), shareUrl), '_blank', 'noopener');
  };

  const privacyDetail =
    capability === 'booking'
      ? tt('v2.app.privacyBooking', 'Bookings, revenue and approvals')
      : tt('v2.app.privacyGeneric', 'Your records and customer details');

  return (
    <section className="screen on" data-screen="app">
      <div className="wrap stack">
        <div className="screen-head">
          <div className="sh-grow row" style="gap:12px">
            <span className="ed-ic" id="appMark" style="width:40px;height:40px;border-radius:12px">{mark}</span>
            <div>
              <div className="row gap8">
                <h2 style="font-size:22px" id="appTitle">{name}</h2>
                <span className={badgeCls} id="appStatusBadge">
                  {badgeLabel}
                  {isPublic ? <Ic n="globe" size={11} /> : null}
                </span>
              </div>
              {tagline ? <p className="sub-h" id="appTagline">{tagline}</p> : null}
            </div>
          </div>
          {shareUrl ? (
            <button className="btn btn-ghost" type="button" onClick={openLive}>
              <Ic n="ext" /> {builtToTry ? t('dlv.try') : tt('v2.app.viewLive', 'View live')}
            </button>
          ) : null}
          <button className="btn btn-primary" type="button" onClick={openEditor}>
            <Ic n="layers" /> {tt('v2.app.editApp', 'Edit app')}
          </button>
        </div>

        <div className="segtabs" data-tabgroup="appctx" style="max-width:340px">
          <button
            className={`segtab${tab === 'overview' ? ' on' : ''}`}
            data-tab="overview"
            data-tabgroup="appctx"
            type="button"
            aria-pressed={tab === 'overview'}
            onClick={() => setTab('overview')}
          >
            <Ic n="phone" /> {t('ad.secOverview')}
          </button>
          <button
            className={`segtab${tab === 'manage' ? ' on' : ''}`}
            data-tab="manage"
            data-tabgroup="appctx"
            type="button"
            aria-pressed={tab === 'manage'}
            onClick={() => setTab('manage')}
          >
            <Ic n="grid" /> {t('ad.secManage')}
          </button>
        </div>

        {/* OVERVIEW */}
        <div className={`ep-panel${tab === 'overview' ? ' on' : ''}`} data-panel="overview" data-tabgroup="appctx">
          <div className="grid-2" style="align-items:start">
            <div style="display:grid;place-items:center;padding:8px 0">
              {/* the spike's hand-built .appui mock → the REAL running app,
                  sandboxed + inert (the same live preview the editor uses) */}
              <div className="device">
                <div className="glass">
                  <AppPreview appId={appId} url={app.url} ogImageUrl={app.ogImageUrl} />
                </div>
              </div>
            </div>

            <div className="stack">
              {hasAnyStat ? (
                <div className="stat-grid" id="appOverviewStats">
                  <div className="stat"><div className="s-num">{fmt(stats?.views)}</div><div className="s-lbl">{t('ad.views')}</div></div>
                  <div className="stat"><div className="s-num">{fmt(stats?.installs)}</div><div className="s-lbl">{t('ad.installs')}</div></div>
                  <div className="stat"><div className="s-num">{fmt(stats?.interactions)}</div><div className="s-lbl">{t('ad.interactions')}</div></div>
                  <div className="stat"><div className="s-num">{fmt(stats?.creditsUsed)}</div><div className="s-lbl">{t('ad.creditsUsed')}</div></div>
                </div>
              ) : (
                // AppDetail's honesty rule — one quiet line, never zero-cards
                <p className="sub-h" style="margin:0">{t('ad.analyticsNote')}</p>
              )}

              {builtToTry && delivery ? <DeliveryNotice delivery={delivery} shareUrl={shareUrl} onTry={openLive} /> : (
              <div className="panel">
                <div className="panel-hd">
                  <span className="l-ico ok"><Ic n="rocket" /></span>
                  <h3>
                    {status === 'live'
                      ? tt('v2.app.liveT', 'Your app is live')
                      : tt('v2.app.previewT', 'Your app is ready to share')}
                  </h3>
                </div>
                <div className="panel-p stack" style="gap:12px">
                  <p className="sub-h" style="margin:0">
                    {tt('v2.app.liveSub', "Send it to your first 5 users, that's how apps find their people.")}
                  </p>
                  {shareUrl ? (
                    <div className="gh-field on-panel">
                      <span className="k">{tt('v2.app.linkK', 'LINK')}</span>
                      <span id="appShareLink" style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{shareDisplay}</span>
                      <button
                        className="ic-act mla"
                        style="background:none"
                        type="button"
                        aria-label={tt('v2.app.ariaCopy', 'Copy link')}
                        onClick={() => void copyLink()}
                      >
                        <Ic n="copy" />
                      </button>
                    </div>
                  ) : null}
                  {shareUrl ? (
                    <div className="row gap8">
                      <button className="btn btn-primary" style="flex:1" type="button" onClick={openLive}>
                        <Ic n="ext" /> {tt('v2.app.openLiveApp', 'Open live app')}
                      </button>
                      <button className="btn btn-tonal" type="button" onClick={share}>
                        <Ic n="share" /> {t('ad.share')}
                      </button>
                    </div>
                  ) : null}
                  <div className="row gap8">
                    {shareUrl ? (
                      <>
                        <button
                          className="btn btn-ghost btn-sm"
                          style="flex:1"
                          type="button"
                          aria-label={tt('v2.app.ariaShareX', 'Share on X')}
                          onClick={() => openChannel('x')}
                        >
                          <Ic n="msg" /> {tt('v2.app.chX', 'X')}
                        </button>
                        <button
                          className="btn btn-ghost btn-sm"
                          style="flex:1"
                          type="button"
                          aria-label={tt('v2.app.ariaShareWa', 'Share on WhatsApp')}
                          onClick={() => openChannel('whatsapp')}
                        >
                          <Ic n="msg" /> {tt('v2.app.chWa', 'WhatsApp')}
                        </button>
                      </>
                    ) : null}
                  </div>
                </div>
              </div>
              )}

              {suggestions.length > 0 ? (
                <div className="panel">
                  <div className="panel-hd"><h3>{tt('v2.app.keepBuilding', 'Keep building, one thing at a time')}</h3></div>
                  <div className="list divide">
                    {suggestions.map((c) => (
                      <button key={c.id} className="lrow clk" type="button" onClick={openEditor}>
                        <span className="l-ico brand"><Ic n={CAP_ICON[c.icon] ?? 'spark'} /></span>
                        <div className="l-txt"><b>{t(c.labelKey)}</b><span>{t(c.whyKey)}</span></div>
                        <span className="l-trail"><Ic n="arrow" /></span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="row gap12" style="justify-content:center;color:var(--faint);font-size:13px;flex-wrap:wrap">
                <span className="badge dot">
                  {isPublic ? <Ic n="globe" /> : <Ic n="lock" />}{' '}
                  {isPublic ? tt('v2.app.public', 'Public share link') : tt('v2.app.private', 'Private, link only')}
                </span>
                <button className="row gap8" style="color:inherit" type="button" onClick={() => setExportOpen(true)}>
                  <Ic n="download" /> {tt('v2.app.exportCode', 'Export code')}
                </button>
                <button className="row gap8" style="color:inherit" type="button" onClick={() => setDomainOpen(true)}>
                  <Ic n="globe" /> {tt('v2.app.customDomain', 'Custom domain')}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* MANAGE — category-aware: the REAL operational panels for THIS app's
            kind of business (AppDetail's exact set), never a fake activity feed. */}
        <div className={`ep-panel${tab === 'manage' ? ' on' : ''}`} data-panel="manage" data-tabgroup="appctx">
          <div className="row row-split" style="margin-bottom:16px">
            <span className="sec-label">{tt('v2.app.manageLabel', 'Management · private to you')}</span>
            {/* the spike's .state-switch is prototype-only — states here are the
                real per-panel fetch lifecycles */}
          </div>

          <div data-statehost="manage" data-state="loaded">
            <div data-when="loaded" className="stack">
              <h3 id="manageTitle" style="font-size:19px;font-weight:700">{t(MANAGE_TITLE_KEY[capability])}</h3>

              {/* REAL server-backed owner consoles — the contract-driven ManageRecords lights up any
                  owner-managed collection (orders, tickets, inventory items/adjustments, quiz attempts,
                  event RSVPs, HR time-off/roster, directory listings) from the shared collection
                  contracts. ownsApp-gated server-side: a non-owner gets an empty list, never a leak. */}
              {/* §CANONICAL-LANE-DESCRIPTOR (launch-ledger A-1/A-5/R-1) — mount a console for EVERY
                  lane the SERVER says this app has, primary AND composed, at their real namespaced
                  ids. `CAPABILITY_OWNER_CONSOLES[capability]` below is the fallback for an app whose
                  descriptor the server could not build; on its own it only ever finds the PRIMARY
                  lane, so a composite app's composed lane — a directory whose customers submit
                  listings onto an events app, say — had NO owner surface at all: the rows existed and
                  the Data Plane returned them, but the owner could not see or moderate them.
                  The same fix already existed in screens/AppDetail.tsx, which this V2 hub replaced —
                  it landed on the screen that no longer renders. */}
              {/* §S7-INVENTORY-CONSOLE — an inventory app's owner could list stock and do nothing else: the lane
                  registers items + adjustments, and both were mounted as the generic list-and-advance console
                  (no add, no adjust, no reorder view — Session 7 inventory pilot, stockpile-25jy). ManageInventory
                  is the stock console built for exactly this (add / edit / archive items, receive · recount ·
                  write-off as one ledger row plus the new on-hand, low/out derived from the reorder point, the
                  adjustment history) and was mounted only on screens/AppDetail.tsx, which no route renders. It
                  writes the bare items/adjustments routes, so it takes the PRIMARY inventory lane; a composed,
                  namespaced inventory lane keeps the generic console. */}
              {lanes?.ownerConsoles?.length
                ? lanes.ownerConsoles.map((con) => (
                    con.category === 'inventory' && con.id === 'adjustments' && lanes.ownerConsoles.some((c) => c.category === 'inventory' && c.id === 'items')
                      ? null
                      : con.category === 'inventory' && con.id === 'items'
                      ? <div key={con.id} className="panel panel-p" data-owner-console="inventory"><ManageInventory appId={appId} /></div>
                      : con.content
                      ? <div key={con.id} className="panel panel-p"><ManageContent appId={appId} collection={con.id} title={con.label} fields={con.content.fields} /></div>
                      : (
                    <div key={con.id} className="panel panel-p">
                      <ManageRecords appId={appId} collection={con.id} titleKey="mgmt.records.title" title={con.label} />
                    </div>
                  )))
                : hasOwnerConsole(capability)
                ? CAPABILITY_OWNER_CONSOLES[capability].map((con) => (
                    <div key={con.collection} className="panel panel-p">
                      <ManageRecords appId={appId} collection={con.collection} titleKey={con.titleKey} />
                    </div>
                  ))
                : null}

              {/* §DEF-OWNERCONSOLE-1 — CRM's REAL owner workspace (contacts + deals CRUD + pipeline
                  stage transitions + activity timeline), fully server-backed via /owner/{contacts,deals}.
                  Replaces the previously-BLANK crm Manage tab (a blank owner console is never acceptable). */}
              {capability === 'crm' ? (
                <div className="panel panel-p"><ManageCRM appId={appId} /></div>
              ) : null}

              {/* honest capability stub for families with NO owner console AND no bespoke panel yet
                  (renders nothing for booking/crm/generic or any capability whose real console is above) */}
              {capability !== 'booking' && capability !== 'crm' && capability !== 'generic' && !hasOwnerConsole(capability) ? (
                <div className="panel panel-p">
                  <AppManagementShell capability={capability} bookingWired={false} />
                </div>
              ) : null}

              {/* Booking — the four REAL server-backed panels (Supabase app_data,
                  ownsApp-gated), in the spike's grid-2 arrangement */}
              {capability === 'booking' ? (
                <div className="grid-2" style="align-items:start">
                  <div className="stack" style="gap:16px">
                    <div className="panel panel-p"><ManageBookings appId={appId} /></div>
                    <div className="panel panel-p"><ManageCustomers appId={appId} /></div>
                  </div>
                  <div className="stack" style="gap:16px">
                    <div className="panel panel-p"><BookingAvailability appId={appId} /></div>
                    <div className="panel panel-p"><PaymentLinkConfig appId={appId} /></div>
                  </div>
                </div>
              ) : null}

              {/* low-frequency panels fold behind disclosures (AppDetail's IA) */}
              {['booking', 'commerce', 'orderahead'].includes(capability) ? (
                <details className="panel">
                  <summary className="panel-hd" style="cursor:pointer">
                    <div className="l-txt"><b>{t('ad.discWaitlist')}</b><span>{t('ad.discWaitlistS')}</span></div>
                    <span className="l-trail"><Ic n="chev" /></span>
                  </summary>
                  <div className="panel-p"><WaitlistPanel appId={appId} /></div>
                </details>
              ) : null}
              <details className="panel">
                <summary className="panel-hd" style="cursor:pointer">
                  <div className="l-txt"><b>{t('ad.discAssets')}</b><span>{t('ad.discAssetsS')}</span></div>
                  <span className="l-trail"><Ic n="chev" /></span>
                </summary>
                <div className="panel-p"><AssetLibraryPanel appId={appId} /></div>
              </details>
              <details className="panel">
                <summary className="panel-hd" style="cursor:pointer">
                  <div className="l-txt"><b>{t('ad.discActivity')}</b><span>{t('ad.discActivityS')}</span></div>
                  <span className="l-trail"><Ic n="chev" /></span>
                </summary>
                <div className="panel-p"><ActivityLogPanel appId={appId} /></div>
              </details>

              <div className="banner info">
                <span className="b-ic"><Ic n="shield" /></span>
                <div>
                  <b>{tt('v2.app.privacyT', 'This management view lives inside Pomela, never in the app.')}</b>
                  <p>
                    {tt('v2.app.privacyLead', 'Your customers only ever see the customer side.')}{' '}
                    <span id="managePrivacyDetail">{privacyDetail}</span>{' '}
                    {tt('v2.app.privacyTail', 'stay private to you here.')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* real sheets — export the project / connect a custom domain */}
      {exportOpen ? <ExportSheet app={app} onClose={() => setExportOpen(false)} /> : null}
      {domainOpen ? <DomainSheet app={app} onClose={() => setDomainOpen(false)} /> : null}
      {/* copy-link and share confirmations render as real toasts on this screen */}
    </section>
  );
}
